const SOURCE_VIEW = Object.freeze({
  requisition: "requisitions",
  requisitions: "requisitions",
  requisition_approval: "approvals",
  requisition_approvals: "approvals",
  rfq: "rfqs",
  rfqs: "rfqs",
  quote: "rfqs",
  quotes: "rfqs",
  purchase_order: "orders",
  purchase_orders: "orders",
  commercial_instrument: "orders",
  commercial_instruments: "orders",
  goods_receipt: "receipts",
  goods_receipts: "receipts",
  fulfilment_record: "receipts",
  fulfilment_records: "receipts",
  invoice: "invoices",
  invoices: "invoices",
  match_run: "invoices",
  match_runs: "invoices",
  transaction_exception: "invoices",
  transaction_exceptions: "invoices",
  payment: "money",
  payment_records: "money",
  reconciliation_record: "money",
  reconciliation_records: "money",
  obligation: "value",
  obligation_ledger: "value",
  coverage_claim: "value",
  coverage_claims: "value",
  recurring_commitment: "value",
  recurring_commitments: "value",
});

export function sourceView(entityType) {
  return SOURCE_VIEW[String(entityType || "").toLowerCase()] || "audit";
}

export function sourceHref(entityType, entityId) {
  const record = entityId ? `&record=${String(entityId)}` : "";
  return `/app?view=${sourceView(entityType)}${record}`;
}

export function orderEvents(events) {
  return [...(events || [])].sort((a, b) => {
    const time = String(b.created_at || "").localeCompare(
      String(a.created_at || ""),
    );
    if (time) return time;
    return String(b.id ?? b.event_id ?? "").localeCompare(
      String(a.id ?? a.event_id ?? ""),
    );
  });
}

const TECHNICAL_ACTIONS = new Set(["insert", "update", "delete"]);

function genericBusinessLabel(event) {
  const action = String(event.action || "").toLowerCase();
  const entity = String(event.entity_type || "").toLowerCase();
  const previous = String(event.previous_state || "").toLowerCase();
  const next = String(event.new_state || "").toLowerCase();
  if (!TECHNICAL_ACTIONS.has(action)) return null;

  if (entity === "requisition") {
    if (action === "insert") return "Request created";
    if (action === "delete") return "Request deleted";
    if (previous === next || !next) return null;
    return {
      submitted: "Request submitted",
      approved: "Request approved",
      rejected: "Request rejected",
      sourced: "Request moved to sourcing",
    }[next] || "Request status changed";
  }
  if (entity === "requisition_approval" && action === "insert")
    return "Approval decision recorded";
  if (entity === "rfq") {
    if (action === "insert") return "RFQ created";
    if (previous !== next && next) return `RFQ ${next.replaceAll("_", " ")}`;
    return null;
  }
  if (entity === "quote")
    return action === "insert" ? "Supplier quote recorded" : null;
  if (entity === "purchase_order")
    return action === "insert" ? "Purchase order created" : null;
  if (entity === "goods_receipt")
    return action === "insert" ? "Goods receipt recorded" : null;
  if (entity === "invoice") {
    if (action === "insert") return "Invoice recorded";
    if (previous !== next && next) return `Invoice ${next.replaceAll("_", " ")}`;
  }
  return null;
}

export function businessActivityEvents(events) {
  return orderEvents(events).flatMap((event) => {
    const action = String(event.action || "").toLowerCase();
    if (!TECHNICAL_ACTIONS.has(action))
      return [{ ...event, display_action: event.action }];
    const displayAction = genericBusinessLabel(event);
    return displayAction ? [{ ...event, display_action: displayAction }] : [];
  });
}

function budgetUsed(budget) {
  return (budget?.budget_commitments || [])
    .filter((item) => item.stage !== "released")
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
}

export function deriveBudgetPositions(budgets) {
  const positions = new Map();
  for (const budget of budgets || []) {
    if (budget.status !== "active") continue;
    const currency = String(budget.currency || "INR").toUpperCase();
    const current = positions.get(currency) || {
      currency,
      allocated: 0,
      consumed: 0,
      budgetCount: 0,
    };
    current.allocated += Number(budget.allocated_amount || 0);
    current.consumed += budgetUsed(budget);
    current.budgetCount += 1;
    positions.set(currency, current);
  }
  return [...positions.values()]
    .map((position) => ({
      ...position,
      remaining: position.allocated - position.consumed,
      consumedPercent: position.allocated
        ? (position.consumed / position.allocated) * 100
        : 0,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function action(id, type, reference, title, detail, dueAt, budget = null) {
  return {
    id: `${type}:${id}`,
    entityId: id,
    entityType: type,
    reference,
    title,
    detail,
    dueAt: dueAt || null,
    href: sourceHref(type, id),
    budget,
  };
}

export function deriveActionItems(data, permissions, currentUserId = null) {
  const items = [];
  const budgets = new Map((data.budgets || []).map((row) => [row.id, row]));
  if (permissions.requisition) {
    for (const row of data.requisitions || []) {
      if (row.created_by !== currentUserId) continue;
      if (row.status === "draft")
        items.push(
          action(
            row.id,
            "requisition",
            row.request_no,
            "Complete requisition",
            row.title,
            row.needed_by,
          ),
        );
      if (row.status === "rejected")
        items.push(
          action(
            row.id,
            "requisition",
            row.request_no,
            "Revise returned requisition",
            row.title,
            row.needed_by,
          ),
        );
    }
  }
  if (permissions.approve) {
    for (const row of data.requisitions || []) {
      const assignedToCurrentUser = (
        row.requisition_approval_assignments || []
      ).some((assignment) => assignment.approver_id === currentUserId);
      if (row.status === "submitted" && assignedToCurrentUser) {
        const budget = budgets.get(row.budget_id);
        const consumed = budget ? budgetUsed(budget) : null;
        items.push(
          action(
            row.id,
            "requisition",
            row.request_no,
            "Approve requisition",
            row.title,
            row.needed_by,
            budget
              ? {
                  currency: budget.currency,
                  consumed,
                  allocated: Number(budget.allocated_amount || 0),
                  consumedPercent: Number(budget.allocated_amount)
                    ? (consumed / Number(budget.allocated_amount)) * 100
                    : 0,
                  category: budget.cost_category,
                }
              : null,
          ),
        );
      }
    }
  }
  if (permissions.procure) {
    for (const row of data.requisitions || []) {
      if (row.status === "approved")
        items.push(
          action(
            row.id,
            "requisition",
            row.request_no,
            "Start sourcing",
            row.title,
            row.needed_by,
          ),
        );
    }
    for (const row of data.rfqs || []) {
      if (row.status === "draft")
        items.push(
          action(
            row.id,
            "rfq",
            row.rfq_no,
            "Issue RFQ",
            row.title,
            row.deadline,
          ),
        );
      if (
        row.status === "published" &&
        row.deadline &&
        new Date(row.deadline).getTime() <= Date.now()
      )
        items.push(
          action(
            row.id,
            "rfq",
            row.rfq_no,
            "Review sourcing responses",
            row.title,
            row.deadline,
          ),
        );
    }
    for (const row of data.orders || []) {
      if (row.status === "draft")
        items.push(
          action(
            row.id,
            "purchase_order",
            row.po_no,
            "Issue purchase order",
            row.vendors?.name || "Approved award",
            null,
          ),
        );
    }
  }
  if (permissions.receive) {
    for (const row of data.orders || []) {
      if (["issued", "part_received"].includes(row.status)) {
        items.push(
          action(
            row.id,
            "purchase_order",
            row.po_no,
            "Record receipt",
            row.vendors?.name || "Issued purchase order",
            null,
          ),
        );
      }
    }
  }
  if (permissions.finance) {
    for (const row of data.invoices || []) {
      if (["recorded", "exception"].includes(row.status)) {
        items.push(
          action(
            row.id,
            "invoice",
            row.invoice_no,
            row.status === "exception"
              ? "Resolve match exception"
              : "Run three-way match",
            row.vendors?.name || row.purchase_orders?.po_no || "Invoice",
            row.invoice_date,
          ),
        );
      } else if (row.status === "matched") {
        items.push(
          action(
            row.id,
            "invoice",
            row.invoice_no,
            "Approve matched invoice",
            row.vendors?.name || row.purchase_orders?.po_no || "Invoice",
            row.invoice_date,
          ),
        );
      }
    }
  }
  return items.sort(
    (a, b) =>
      String(a.dueAt || "9999").localeCompare(String(b.dueAt || "9999")) ||
      a.reference.localeCompare(b.reference),
  );
}

const PLACEHOLDER =
  /^(tbd|to be confirmed|n\/a|na|unknown|as discussed|standard)$/i;

function state(key, label, status, reason, source, href, supported = true) {
  return { key, label, status, reason, source, href, supported };
}

export function deriveRequirementStates(requisition, attachments = []) {
  const href = sourceHref("requisition", requisition.id);
  const lines = requisition.requisition_lines || [];
  const checks = [
    state(
      "purpose",
      "Purpose and business need",
      requisition.title?.trim() && requisition.description?.trim()
        ? "Required"
        : "Needs confirmation",
      requisition.title?.trim() && requisition.description?.trim()
        ? "A title and business description are recorded."
        : "Add a clear title and business reason before approval.",
      "Requisition title and description",
      href,
    ),
    state(
      "need_by",
      "Need-by date",
      requisition.needed_by ? "Required" : "Needs confirmation",
      requisition.needed_by
        ? "The requested delivery date is recorded."
        : "No delivery date is recorded.",
      "Requisition needed_by",
      href,
    ),
    state(
      "delivery",
      "Delivery location",
      requisition.location_id ? "Required" : "Needs confirmation",
      requisition.location_id
        ? "A governed organisation location is linked."
        : "No delivery location is linked.",
      "Requisition location_id",
      href,
    ),
    state(
      "lines",
      "Requested lines",
      lines.length ? "Required" : "Needs confirmation",
      lines.length
        ? `${lines.length} line item${lines.length === 1 ? "" : "s"} recorded.`
        : "At least one item or service line is required.",
      "Requisition lines",
      href,
    ),
    state(
      "specification",
      "Line specifications",
      lines.length &&
        lines.every(
          (line) =>
            line.specification?.trim() &&
            !PLACEHOLDER.test(line.specification.trim()),
        )
        ? "Required"
        : "Needs confirmation",
      lines.length &&
        lines.every(
          (line) =>
            line.specification?.trim() &&
            !PLACEHOLDER.test(line.specification.trim()),
        )
        ? "Every line contains a non-placeholder specification."
        : "One or more line specifications are missing or look provisional.",
      "Requisition line specification",
      href,
    ),
    state(
      "quantity",
      "Quantity and unit",
      lines.length &&
        lines.every((line) => Number(line.quantity) > 0 && line.unit?.trim())
        ? "Required"
        : "Needs confirmation",
      lines.length &&
        lines.every((line) => Number(line.quantity) > 0 && line.unit?.trim())
        ? "Every line has a positive quantity and unit."
        : "One or more lines need a quantity or unit.",
      "Requisition line quantity and unit",
      href,
    ),
    state(
      "value",
      "Estimated value",
      Number(requisition.estimated_value) > 0
        ? "Recommended"
        : "Needs confirmation",
      Number(requisition.estimated_value) > 0
        ? "A non-zero planning estimate is recorded."
        : "No meaningful planning estimate is recorded.",
      "Requisition estimated_value",
      href,
    ),
    state(
      "attachments",
      "Supporting evidence",
      attachments.length ? "Recommended" : "Needs confirmation",
      attachments.length
        ? `${attachments.length} attachment${attachments.length === 1 ? "" : "s"} recorded.`
        : "No requirement attachment is recorded; confirm whether evidence is needed.",
      "Attachments linked to requisition",
      href,
    ),
    state(
      "commercial",
      "Commercial expectations",
      "Needs confirmation",
      "The current requisition schema has no dedicated commercial-expectations field. Confirm this in the RFQ or specification.",
      "Unsupported by current requisition schema",
      href,
      false,
    ),
    state(
      "assumptions",
      "Assumptions",
      "Needs confirmation",
      "RAQOZ does not persist structured assumptions in the current schema. Resolve assumptions in source fields; this label is not saved.",
      "Unsupported by current requisition schema",
      href,
      false,
    ),
  ];
  return checks;
}

export function buildApprovalBrief({
  requisition,
  approvals = [],
  attachments = [],
  rfq = null,
  quotes = [],
  purchaseOrder = null,
}) {
  const requirements = deriveRequirementStates(requisition, attachments);
  return {
    generatedAt: new Date().toISOString(),
    sourceHref: sourceHref("requisition", requisition.id),
    confirmed: {
      purpose: requisition.title,
      businessNeed: requisition.description || null,
      requester: requisition.profiles?.full_name || null,
      location: requisition.locations?.name || null,
      needBy: requisition.needed_by || null,
      estimatedValue:
        requisition.estimated_value !== null &&
        requisition.estimated_value !== undefined &&
        requisition.estimated_value !== "" &&
        Number.isFinite(Number(requisition.estimated_value))
          ? Number(requisition.estimated_value)
          : null,
      status: requisition.status,
      lines: requisition.requisition_lines || [],
      attachments,
      approvalHistory: approvals,
      sourcing: rfq ? { rfq, quotes, purchaseOrder } : null,
    },
    derived: {
      lineCount: (requisition.requisition_lines || []).length,
      approvalCount: approvals.filter((item) => item.decision === "approved")
        .length,
      exceptionCount: requirements.filter(
        (item) => item.status === "Needs confirmation",
      ).length,
      requirementStates: requirements,
    },
    assumptions: requirements.filter((item) => item.key === "assumptions"),
    missing: requirements.filter(
      (item) => item.status === "Needs confirmation",
    ),
  };
}

export function evidenceCoverage(pack) {
  const receiptDiscrepancies =
    pack.receipt_discrepancies ||
    pack.goods_receipts
      ?.flatMap((receipt) => receipt.goods_receipt_lines || [])
      .filter((line) => Number(line.rejected_qty || 0) > 0);
  const supersededDocuments =
    pack.superseded_document_references ||
    pack.document_versions?.filter(
      (version) => Number(version.version_number || 0) > 1,
    );
  const recordCount = (data) =>
    Array.isArray(data) ? data.length : data ? 1 : 0;
  const category = (key, schemaSupported, data, partial = false) => {
    if (!schemaSupported)
      return { category: key, status: "Unsupported by current schema" };
    const count = recordCount(data);
    return {
      category: key,
      status: count
        ? partial
          ? "Partially available"
          : "Available"
        : "Not recorded",
      recordCount: count,
    };
  };
  const multipartCategory = (key, parts) => {
    const counts = parts.map(recordCount);
    const populated = counts.filter(Boolean).length;
    return {
      category: key,
      status:
        populated === parts.length
          ? "Available"
          : populated
            ? "Partially available"
            : "Not recorded",
      recordCount: counts.reduce((sum, count) => sum + count, 0),
    };
  };
  return [
    category(
      "Requisition ancestry",
      true,
      pack.requisition || pack.case?.requisition_id,
    ),
    category(
      "RFQ ancestry",
      true,
      pack.rfq ||
        pack.links?.filter?.((row) =>
          String(row.to_entity_type).includes("rfq"),
        ),
    ),
    multipartCategory("Quotation and award ancestry", [
      pack.quotes,
      pack.award,
    ]),
    category("PO issue details", true, pack.instruments),
    category("PO versions and amendments", true, pack.document_versions),
    category(
      "Superseded-document references",
      true,
      supersededDocuments,
      !pack.superseded_document_references && recordCount(supersededDocuments),
    ),
    category("Supplier acknowledgement", true, pack.acknowledgements),
    category(
      "GRN and fulfilment",
      true,
      pack.fulfilment || pack.goods_receipts,
    ),
    category(
      "Receipt discrepancies",
      true,
      receiptDiscrepancies ||
        pack.exceptions?.filter?.((row) =>
          String(row.exception_type || "").includes("receipt"),
        ),
    ),
    multipartCategory("Invoice and match results", [
      pack.invoices,
      pack.match_runs,
    ]),
    category("Match exceptions", true, pack.exceptions),
    category("Payment-state evidence", true, pack.payments),
    category("Reconciliation evidence", true, pack.reconciliations),
    category("Closure assessment", true, pack.case?.closure_status),
    category("Approval history", true, pack.approvals || pack.approval_history),
    category("Immutable events", true, pack.timeline),
    category("Attachments/references", true, pack.attachments || pack.evidence),
    category(
      "Dispatch evidence",
      true,
      pack.fulfilment?.filter?.((row) => row.kind === "goods_dispatch"),
    ),
    category(
      "Inspection evidence",
      true,
      pack.fulfilment?.filter?.((row) => row.kind === "inspection"),
    ),
    category("Bank-executed payment confirmation", false, null),
    category("Bank-feed reconciliation", false, null),
    category("Batch, lot and expiry", false, null),
  ];
}

export function moneyAvailability(payments, adjustments) {
  const byCurrency = (rows, value) =>
    rows.reduce((map, row) => {
      const currency = row.currency || "UNKNOWN";
      map[currency] = (map[currency] || 0) + Number(value(row) || 0);
      return map;
    }, {});
  const paidRows = (payments || []).filter((row) =>
    ["paid", "reconciled"].includes(row.state),
  );
  const unreconciledRows = (payments || []).filter(
    (row) => row.state === "paid",
  );
  const advanceRows = (adjustments || []).filter(
    (row) => row.adjustment_type === "advance",
  );
  const retentionRows = (adjustments || []).filter(
    (row) => row.adjustment_type === "retention",
  );
  return {
    paid: paidRows.length
      ? {
          available: true,
          values: byCurrency(paidRows, (row) => row.net_amount),
          records: paidRows.length,
        }
      : {
          available: false,
          reason: "No RAQOZ payment-completion evidence is recorded.",
        },
    unreconciled: unreconciledRows.length
      ? {
          available: true,
          values: byCurrency(unreconciledRows, (row) => row.net_amount),
          records: unreconciledRows.length,
        }
      : {
          available: false,
          reason: "No paid-but-unreconciled RAQOZ payment record exists.",
        },
    advances: advanceRows.length
      ? {
          available: true,
          values: byCurrency(advanceRows, (row) => row.amount),
          records: advanceRows.length,
        }
      : {
          available: false,
          reason:
            "No advance adjustment is recorded; RAQOZ cannot infer advances from invoices.",
        },
    retentions: retentionRows.length
      ? {
          available: true,
          values: byCurrency(retentionRows, (row) => row.amount),
          records: retentionRows.length,
        }
      : {
          available: false,
          reason:
            "No retention adjustment is recorded; RAQOZ cannot infer retention from payment terms.",
        },
  };
}
